import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { getNowTimeString, getUtcOffset } from 'utils/datetime'
import { datadogLogs } from '@datadog/browser-logs'
import { useContext } from 'react'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import {
  CategoricalChoice,
  ConjointPanelResponse as PanelResponse
} from '@focaldata/cin-ui-components/lib/components/SurveyQuestion/Conjoint/ConjointItem.types'
import { useQuestionnaireParams } from './useQuestionnaireParams'
import { usePostCallbackWithRetry } from './usePostCallback'

interface ConjointPackageAttributeOption {
  position: number
  attribute_id: string
  attribute_option_id: string
}
interface ConjointPackageResponse {
  conjoint_package_id: string
  position: number
  choice_value: CategoricalChoice
  attribute_options: ConjointPackageAttributeOption[]
}

interface CategoricalOptionChoice {
  response_option_id: string
  categorical_choice: CategoricalChoice
  text_entry: string
  rendered_response_option_position: number
}

interface ConjointPanelResponse {
  conjoint_panel_id: string
  default_response_choice?: CategoricalOptionChoice
  position: number
  package_selections: ConjointPackageResponse[]
}

interface SendConjointResponsePayload {
  survey_id: string
  supplier_respondent_id: string
  panel_supplier_code: string
  rendered_entry_position: number
  question_id: string
  panel_responses: ConjointPanelResponse[]
  entering_datetime: string
  submission_datetime: string
  time_zone: string
  utc_offset: string
  is_preview: boolean
}

const transformToApiFormat = (
  panelResponses: PanelResponse[]
): ConjointPanelResponse[] => {
  return panelResponses.map((panel) => ({
    conjoint_panel_id: panel.conjointPanelId,
    default_response_choice: panel.defaultResponseChoice
      ? {
          response_option_id: panel.defaultResponseChoice.responseOptionId,
          categorical_choice: panel.defaultResponseChoice.categoricalChoice,
          text_entry: panel.defaultResponseChoice.textEntry,
          rendered_response_option_position:
            panel.defaultResponseChoice.renderedResponseOptionPosition
        }
      : undefined,
    position: panel.position,
    package_selections:
      panel.packageSelections?.map((pkg) => ({
        conjoint_package_id: pkg.conjointPackageId,
        position: pkg.position,
        choice_value: pkg.choiceValue,
        attribute_options: pkg.attributeOptions.map((attr) => ({
          position: attr.position,
          attribute_id: attr.attributeId,
          attribute_option_id: attr.attributeOptionId
        }))
      })) || []
  }))
}

const useSendConjointResponse = () => {
  const post = usePostCallbackWithRetry<SendConjointResponsePayload>()
  const { surveyId, respondentId, panelSupplier, preview } =
    useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress],
    addResponseChoicePromise
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendConjointResponse = async ({
    renderedEntryPosition,
    questionId,
    panelResponses,
    enteringDatetime
  }: {
    renderedEntryPosition: number
    questionId: string
    panelResponses: PanelResponse[]
    enteringDatetime: string
  }) => {
    const payload: SendConjointResponsePayload = {
      survey_id: surveyId,
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier,
      rendered_entry_position: renderedEntryPosition,
      question_id: questionId,
      panel_responses: transformToApiFormat(panelResponses),
      entering_datetime: enteringDatetime,
      submission_datetime: getNowTimeString(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset(),
      is_preview: !!preview
    }
    const url = coreRespondentUrl(`/surveys/${surveyId}/responses/conjoint`)

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Conjoint response was sent', {
          payload,
          url,
          requestDurationMs: Math.round(requestDuration)
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendConjointResponse'
          )
        }
        datadogLogs.logger.error(
          'Network Failure: Conjoint response failed to be sent',
          {
            err,
            payload,
            url,
            requestDurationMs: Math.round(requestDuration)
          }
        )
      })

    // Add the promise to the tracker
    addResponseChoicePromise(promise)
  }

  return { sendConjointResponse }
}

export default useSendConjointResponse
